/**
 * Timezone-aware calendar-day math for the scheduler grid.
 *
 * The custom calendar historically computed day boundaries and event slot
 * positions with `date-fns` (`startOfDay`, `.getHours()`, `.setHours()`), which
 * silently anchor everything to the *browser's* timezone. That means a user who
 * picks a display timezone (or whose clinic location is in another zone) still
 * sees the grid laid out in their own browser zone.
 *
 * These helpers replace that browser-local math with math anchored to an
 * explicit `timeZone`, using `Intl.DateTimeFormat` to assemble wall-clock parts
 * and resolve the zone offset against a candidate instant, so we don't add a
 * date library.
 *
 * This module is the single timezone kernel for the console: `closure-dates.ts`
 * and `schedule-utils.ts` import `tzOffsetMs` / `zonedDateKey` from here rather
 * than carrying their own copies, so DST, sub-second and invalid-zone behavior
 * can't drift apart between scheduling surfaces.
 *
 * INVARIANT: when `timeZone` equals the system/browser timezone, every helper
 * returns exactly what the old `date-fns` / local-`Date` math returned — so the
 * common case (resolved timezone === browser timezone) is byte-for-byte
 * unchanged.
 *
 * Two notions of "which day" — the suffix is the contract, so read it before
 * picking a helper: bare names (`zonedDayStart`) take a MARKER, `*OfInstant`
 * names take an absolute instant. `zonedDayMarker(instant, tz)` converts one to
 * the other when a call site holds the wrong kind.
 *  - MARKER semantics (`zonedDayStart`, `zonedDayEnd`, `zonedInstantOnDay`):
 *    the input `Date` is a calendar-day marker whose LOCAL y/m/d encodes the
 *    intended calendar date (this is how the calendar constructs its day/column
 *    dates). We read those local fields and ignore the marker's instant.
 *  - INSTANT semantics (`zonedDayStartOfInstant`, `zonedDayEndOfInstant`,
 *    `zonedWallMinutes`, `isSameZonedDay`): the input `Date` is an absolute
 *    instant and we want the calendar day / wall time it maps to *in `timeZone`*.
 */
import { toMs } from "@/lib/timestamp";

// `Intl.DateTimeFormat` construction is expensive; the grid resolves offsets
// thousands of times per render, so cache one formatter per zone.
const dtfCache = new Map<string, Intl.DateTimeFormat>();

function buildFormatter(timeZone: string): Intl.DateTimeFormat {
  const opts: Intl.DateTimeFormatOptions = {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  };
  try {
    return new Intl.DateTimeFormat("en-US", opts);
  } catch {
    // An invalid IANA name (e.g. a mistyped location timezone) makes
    // Intl throw RangeError. Fall back to UTC so the calendar renders
    // instead of crashing the whole page — but warn so the misconfigured
    // zone is discoverable rather than silently producing wrong times.
    console.warn(
      `[timezone] invalid IANA timezone "${timeZone}" — falling back to UTC`,
    );
    return new Intl.DateTimeFormat("en-US", { ...opts, timeZone: "UTC" });
  }
}

function zonedFormatter(timeZone: string): Intl.DateTimeFormat {
  let dtf = dtfCache.get(timeZone);
  if (!dtf) {
    dtf = buildFormatter(timeZone);
    dtfCache.set(timeZone, dtf);
  }
  return dtf;
}

/** Offset in ms such that `localWallMs = utcMs + offset` for `timeZone` at `instant`. */
export function tzOffsetMs(timeZone: string, instant: string | Date): number {
  const instantMs = toMs(instant);
  const parts = zonedFormatter(timeZone).formatToParts(instantMs);
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value);
  const localWallMs = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
    get("second"),
  );
  // `localWallMs` is second-granular (Intl stops at seconds), so compare against
  // the instant truncated to whole seconds — otherwise the sub-second part leaks
  // into the offset (e.g. a .789s instant would report a -789ms offset error).
  const utcSeconds = Math.floor(instantMs / 1000) * 1000;
  return localWallMs - utcSeconds;
}

/**
 * Instant of a wall-clock time (y-m-d hh:mm) interpreted in `timeZone`. Resolves
 * the offset in two passes so wall times near a DST transition map to the
 * correct UTC instant.
 *
 * Spring-forward gap: a wall time in the skipped hour has no valid instant. The
 * two-pass result would land *before* the gap (e.g. NY 02:30 on a spring-forward
 * day would resolve to 01:30 EST). We detect that and clamp forward to the
 * post-transition instant (03:30 EDT), so click/paste/drop never move a slot
 * backward across the gap.
 */
function wallTimeInstant(
  year: number,
  month1: number, // 1-based
  day: number,
  hours: number,
  minutes: number,
  timeZone: string,
): Date {
  const pretendUtc = Date.UTC(year, month1 - 1, day, hours, minutes, 0);
  const firstOffset = tzOffsetMs(timeZone, new Date(pretendUtc));
  const firstPass = pretendUtc - firstOffset;
  const secondOffset = tzOffsetMs(timeZone, new Date(firstPass));
  const candidate = new Date(pretendUtc - secondOffset);
  // If the candidate doesn't read back with the offset we used, the requested
  // wall time falls in a spring-forward gap; clamp forward with the
  // pre-transition offset.
  if (tzOffsetMs(timeZone, candidate) !== secondOffset) {
    return new Date(pretendUtc - firstOffset);
  }
  return candidate;
}

/** Wall-clock y/m/d and minutes-from-midnight of `instant` in `timeZone`. */
function zonedParts(
  instant: string | Date,
  timeZone: string,
): { year: number; month1: number; day: number; minutes: number } {
  const localWallMs = toMs(instant) + tzOffsetMs(timeZone, instant);
  const wall = new Date(localWallMs); // read via UTC getters = wall-clock fields
  return {
    year: wall.getUTCFullYear(),
    month1: wall.getUTCMonth() + 1,
    day: wall.getUTCDate(),
    minutes: wall.getUTCHours() * 60 + wall.getUTCMinutes(),
  };
}

// ── Marker semantics (input Date's LOCAL y/m/d = the calendar date) ──────────

/** Instant of 00:00 (in `timeZone`) on the calendar date the marker represents. */
export function zonedDayStart(calendarDay: Date, timeZone: string): Date {
  return wallTimeInstant(
    calendarDay.getFullYear(),
    calendarDay.getMonth() + 1,
    calendarDay.getDate(),
    0,
    0,
    timeZone,
  );
}

/** Last millisecond of the calendar date the marker represents, in `timeZone`. */
export function zonedDayEnd(calendarDay: Date, timeZone: string): Date {
  // Next calendar day (local construction handles month/year rollover), then a
  // millisecond before its zoned midnight — mirrors date-fns `endOfDay`.
  const next = new Date(
    calendarDay.getFullYear(),
    calendarDay.getMonth(),
    calendarDay.getDate() + 1,
  );
  return new Date(zonedDayStart(next, timeZone).getTime() - 1);
}

/**
 * Instant at `minutesFromMidnight` (in `timeZone`) on the calendar date the
 * marker represents. Used to turn a grid click / drop into a real start instant.
 */
export function zonedInstantOnDay(
  calendarDay: Date,
  minutesFromMidnight: number,
  timeZone: string,
): Date {
  return wallTimeInstant(
    calendarDay.getFullYear(),
    calendarDay.getMonth() + 1,
    calendarDay.getDate(),
    // Date.UTC normalizes a minute count of either sign, so a negative
    // offset (prep before midnight) lands on the previous day intact.
    0,
    minutesFromMidnight,
    timeZone,
  );
}

// ── Instant semantics (input is an absolute instant: an API timestamp string or a Date) ──

/** Minutes from midnight of `instant`, measured in `timeZone` (0..1439). */
export function zonedWallMinutes(
  instant: string | Date,
  timeZone: string,
): number {
  return zonedParts(instant, timeZone).minutes;
}

/**
 * Calendar fields of `instant` in `timeZone`: weekday (0=Sun..6=Sat), 1-based
 * month, day-of-month, and year. Use for recurrence rules (BYDAY/BYMONTHDAY)
 * so the pattern matches the clinic-zone date rather than the browser's.
 */
export function zonedCalendarParts(
  instant: string | Date,
  timeZone: string,
): { weekday: number; day: number; month1: number; year: number } {
  const { year, month1, day } = zonedParts(instant, timeZone);
  const weekday = new Date(year, month1 - 1, day).getDay();
  return { weekday, day, month1, year };
}

/**
 * Keep the calendar DAY of `instant` (in `timeZone`) but set its wall-clock
 * time to `minutesFromMidnight`. DST-safe. Used by time pickers so typing a
 * clock time means that time in the clinic zone, not the browser's.
 */
export function zonedSetWallMinutes(
  instant: string | Date,
  minutesFromMidnight: number,
  timeZone: string,
): Date {
  const { year, month1, day } = zonedParts(instant, timeZone);
  return wallTimeInstant(
    year,
    month1,
    day,
    // Date.UTC normalizes a minute count of either sign, so a negative
    // offset (prep before midnight) lands on the previous day intact.
    0,
    minutesFromMidnight,
    timeZone,
  );
}

/**
 * The instant showing the same wall-clock date and time in `to` as `instant`
 * does in `from`: 9:00 at one location stays 9:00 at the other. The second
 * of a repeated fall-back time stays the second where `to` repeats it too.
 */
export function rebaseWallTime(instant: Date, from: string, to: string): Date {
  const first = zonedInstantOnDay(
    zonedDayMarker(instant, from),
    zonedWallMinutes(instant, from),
    to,
  );
  if (!isRepeatedOccurrence(instant, from)) return first;
  return repeatedOccurrence(first, to) ?? first;
}

/** The later instant showing the same wall time as `start`, when clocks fall back just after it. */
function repeatedOccurrence(start: Date, timeZone: string): Date | null {
  const foldMs =
    tzOffsetMs(timeZone, start) -
    tzOffsetMs(timeZone, new Date(start.getTime() + 3 * 60 * 60 * 1000));
  if (foldMs <= 0) return null;
  const later = new Date(start.getTime() + foldMs);
  return zonedWallMinutes(later, timeZone) === zonedWallMinutes(start, timeZone)
    ? later
    : null;
}

/** Whether `instant` is the second showing of its wall time, after clocks fell back. */
function isRepeatedOccurrence(instant: Date, timeZone: string): boolean {
  const foldMs =
    tzOffsetMs(timeZone, new Date(instant.getTime() - 3 * 60 * 60 * 1000)) -
    tzOffsetMs(timeZone, instant);
  if (foldMs <= 0) return false;
  const earlier = new Date(instant.getTime() - foldMs);
  return (
    zonedWallMinutes(earlier, timeZone) === zonedWallMinutes(instant, timeZone)
  );
}

/**
 * `start` is the first occurrence of its wall time. When clocks fall back
 * just after it, the same wall time comes round again; take that later one
 * when `reference` is on the far side of the change.
 */
export function matchingOccurrence(
  start: Date,
  reference: string | Date,
  timeZone: string,
): Date {
  const later = repeatedOccurrence(start, timeZone);
  return later &&
    tzOffsetMs(timeZone, reference) === tzOffsetMs(timeZone, later)
    ? later
    : start;
}

/**
 * A wall time rebuilt from form fields (always the first occurrence), kept on
 * `saved`'s side of a fall-back hour while it is still on `saved`'s day. Form
 * fields can't tell the two 1:30s apart; an untouched one must not move.
 */
export function keepSavedOccurrence(
  rebuilt: Date,
  saved: string | Date | null | undefined,
  timeZone: string,
): Date {
  return saved &&
    zonedDateKey(saved, timeZone) === zonedDateKey(rebuilt, timeZone)
    ? matchingOccurrence(rebuilt, saved, timeZone)
    : rebuilt;
}

/**
 * Combine the calendar DAY of `dayInstant` with the wall-clock TIME of
 * `timeInstant`, both measured in `timeZone`. Used by date pickers that change
 * the day while preserving the clinic-zone time-of-day.
 */
export function zonedCombineDayAndTime(
  dayInstant: string | Date,
  timeInstant: string | Date,
  timeZone: string,
): Date {
  const day = zonedParts(dayInstant, timeZone);
  const time = zonedParts(timeInstant, timeZone);
  return wallTimeInstant(
    day.year,
    day.month1,
    day.day,
    Math.floor(time.minutes / 60),
    time.minutes % 60,
    timeZone,
  );
}

/**
 * A calendar-day MARKER (local-midnight `Date` whose local y/m/d encode the
 * date) for the day `instant` falls on in `timeZone`. Use to normalize an
 * absolute event instant into the marker the calendar's day-based logic expects,
 * so a browser west/east of the clinic doesn't select the adjacent day.
 */
export function zonedDayMarker(instant: string | Date, timeZone: string): Date {
  const { year, month1, day } = zonedParts(instant, timeZone);
  return new Date(year, month1 - 1, day);
}

/**
 * `YYYY-MM-DD` calendar-day key of `instant` in `timeZone`. A string that is
 * already a calendar day names that day in every zone, so it passes through.
 */
export function zonedDateKey(instant: string | Date, timeZone: string): string {
  if (typeof instant === "string" && /^\d{4}-\d{2}-\d{2}$/.test(instant)) {
    return instant;
  }
  const { year, month1, day } = zonedParts(instant, timeZone);
  return `${year}-${String(month1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Instant of 00:00 (in `timeZone`) on the calendar day `instant` falls on. */
export function zonedDayStartOfInstant(
  instant: string | Date,
  timeZone: string,
): Date {
  const { year, month1, day } = zonedParts(instant, timeZone);
  return wallTimeInstant(year, month1, day, 0, 0, timeZone);
}

/** Last millisecond (in `timeZone`) of the calendar day `instant` falls on. */
export function zonedDayEndOfInstant(
  instant: string | Date,
  timeZone: string,
): Date {
  // A civil day isn't always 86_400_000ms (DST), so derive the end from the
  // next zoned midnight rather than adding a fixed day — matches `zonedDayEnd`.
  const { year, month1, day } = zonedParts(instant, timeZone);
  const nextMidnight = wallTimeInstant(year, month1, day + 1, 0, 0, timeZone);
  return new Date(nextMidnight.getTime() - 1);
}

/**
 * True when two instants fall on the same calendar day in `timeZone`. Replaces
 * browser-local `startOfDay(a) === startOfDay(b)` comparisons.
 */
export function isSameZonedDay(
  a: string | Date,
  b: string | Date,
  timeZone: string,
): boolean {
  const pa = zonedParts(a, timeZone);
  const pb = zonedParts(b, timeZone);
  return pa.year === pb.year && pa.month1 === pb.month1 && pa.day === pb.day;
}

/**
 * A calendar-day MARKER (a local-midnight `Date` whose local y/m/d encodes the
 * date) for "today" in `timeZone`. Its `.toDateString()` can be compared against
 * the calendar's column markers to detect the current day in the display zone.
 */
export function zonedTodayMarker(
  timeZone: string,
  now: Date = new Date(),
): Date {
  return zonedDayMarker(now, timeZone);
}

/**
 * `instant` as a browser-local Date whose fields read `timeZone`'s wall clock,
 * for controls that edit browser-local fields. `wallClockDateToInstant` is the
 * inverse.
 */
export function zonedWallClockDate(
  instant: string | Date,
  timeZone: string,
): Date {
  const { year, month1, day } = zonedCalendarParts(instant, timeZone);
  const minutes = zonedWallMinutes(instant, timeZone);
  return new Date(
    year,
    month1 - 1,
    day,
    Math.floor(minutes / 60),
    minutes % 60,
  );
}

/** The instant a `zonedWallClockDate` Date stands for on `timeZone`. */
export function wallClockDateToInstant(wall: Date, timeZone: string): Date {
  return zonedInstantOnDay(
    new Date(wall.getFullYear(), wall.getMonth(), wall.getDate()),
    wall.getHours() * 60 + wall.getMinutes(),
    timeZone,
  );
}
